<template>
    <div class="news-page">
        <div class="d-flex align-items-center justify-content-center pt-3">
            <h1 class="text-uppercase text-black display-header oswald-bold">News</h1>
        </div>

        <div class="container py-5 news-content">
            <!-- Loading state -->
            <div v-if="loading" class="text-center py-5">
                <span class="fs-5 open-sans">Loading...</span>
            </div>

            <!-- Error state -->
            <div v-else-if="error" class="text-center py-5">
                <span class="fs-5 open-sans text-danger">Failed to load posts.</span>
            </div>

            <template v-else>
                <!-- Posts grid -->
                <div class="row g-4" :class="{ 'paginating': paginating }">
                    <div v-if="posts.length === 0" class="col-12 text-center py-5">
                        <span class="fs-5 open-sans">No posts yet. Check back soon!</span>
                    </div>
                    <div v-for="(post, index) in posts" :key="post.id"
                        class="col-md-6 col-lg-4 news-card"
                        :style="{ transitionDelay: `${index * 0.08}s` }"
                        @click="goToPost(post.slug)"
                        style="cursor: pointer;">
                        <div class="news-card-inner h-100 d-flex flex-column">
                            <div class="news-card-img-wrap">
                                <img v-if="post.cover_image"
                                    :src="`${$API_ASSETS}${post.cover_image.id}?width=800`"
                                    class="news-card-img" alt="" />
                                <div v-else class="news-card-img-placeholder"></div>
                            </div>
                            <div class="news-card-body d-flex flex-column flex-grow-1 p-3">
                                <span class="news-date open-sans">{{ formatDate(post.date_published) }}</span>
                                <h2 class="news-title oswald-bold text-uppercase mt-1 mb-2">{{ post.title }}</h2>
                                <p class="news-excerpt open-sans flex-grow-1">{{ post.excerpt }}</p>
                                <div v-if="post.author" class="news-author d-flex align-items-center mt-3 pt-3 gap-3">
                                    <img v-if="post.author.headshot"
                                        :src="`${$API_ASSETS}${post.author.headshot.id}.png?width=120&height=120&fit=cover`"
                                        class="news-author-avatar" alt="" />
                                    <div v-else class="news-author-avatar-placeholder"></div>
                                    <div class="d-flex flex-column">
                                        <span class="news-author-label open-sans">Written by</span>
                                        <span class="open-sans-500 news-author-name">{{ post.author.name }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Pagination -->
                <div v-if="totalPages > 1" class="d-flex align-items-center justify-content-center gap-2 mt-5">
                    <button class="pagination-btn" :disabled="currentPage === 1" @click="goToPage(currentPage - 1)">
                        <i class="fa-solid fa-chevron-left"></i>
                    </button>
                    <button v-for="p in totalPages" :key="p"
                        class="pagination-btn"
                        :class="{ active: p === currentPage }"
                        @click="goToPage(p)">
                        {{ p }}
                    </button>
                    <button class="pagination-btn" :disabled="currentPage === totalPages" @click="goToPage(currentPage + 1)">
                        <i class="fa-solid fa-chevron-right"></i>
                    </button>
                </div>
            </template>
        </div>

        <Footer />
    </div>
</template>

<script>
import { useRoute } from 'vue-router';
import { useHead } from '@vueuse/head';
import axios from 'axios';
import Footer from '@/components/Footer.vue';

const PER_PAGE = 6;

export default {
    name: 'News',
    setup() {
        const route = useRoute();
        useHead({
            title: route.meta.title || 'Atlas Improv – News',
            meta: [{ name: 'description', content: route.meta.description || 'News and updates from Atlas Improv.' }]
        });
        return {};
    },
    components: { Footer },
    data() {
        return {
            posts: [],
            total: 0,
            currentPage: 1,
            loading: true,
            paginating: false,
            error: false,
        };
    },
    computed: {
        totalPages() {
            return Math.ceil(this.total / PER_PAGE);
        },
    },
    methods: {
        goToPost(slug) {
            this.$router.push(`/news/${slug}`);
        },
        formatDate(dateStr) {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        },
        async goToPage(page) {
            this.currentPage = page;
            this.paginating = true;
            await this.fetchPosts();
            this.paginating = false;
        },
        async fetchPosts() {
            if (this.posts.length === 0) this.loading = true;
            this.error = false;
            const offset = (this.currentPage - 1) * PER_PAGE;
            try {
                const response = await axios.post(this.$API_GRAPHQL, {
                    query: `
                        query {
                            news_post_aggregated(filter: { status: { _eq: "published" } }) {
                                count { id }
                            }
                            news_post(
                                filter: { status: { _eq: "published" } }
                                sort: ["-date_published"]
                                limit: ${PER_PAGE}
                                offset: ${offset}
                            ) {
                                id
                                title
                                slug
                                excerpt
                                date_published
                                cover_image { id }
                                author {
                                    id
                                    name
                                    headshot { id }
                                }
                            }
                        }
                    `
                }, {
                    headers: {
                        'Authorization': `Bearer Pm4Nd5_e46HaK2xtkPcla2PMBzbk9AQf`,
                        'Content-Type': 'application/json'
                    }
                });
                const data = response.data.data;
                this.posts = data.news_post || [];
                this.total = data.news_post_aggregated?.[0]?.count?.id || 0;
            } catch (e) {
                console.error('Error fetching news posts:', e);
                this.error = true;
            } finally {
                this.loading = false;
            }
        },
        observeCards() {
            if (this._cardObserver) this._cardObserver.disconnect();
            const cards = this.$el.querySelectorAll('.news-card');
            this._cardObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('news-card--visible');
                        this._cardObserver.unobserve(entry.target);
                    }
                });
            }, { threshold: 0.08 });
            cards.forEach(card => this._cardObserver.observe(card));
        }
    },
    created() {
        this.fetchPosts();
    },
    updated() {
        this.$nextTick(() => this.observeCards());
    },
    mounted() {
        this.observeCards();
    },
    beforeUnmount() {
        if (this._cardObserver) this._cardObserver.disconnect();
    }
};
</script>

<style scoped>
.news-page {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
}

.news-content {
    flex: 1 0 auto;
}

.news-page :deep(footer),
.news-page :deep(.footer) {
    flex-shrink: 0;
}

.news-card {
    opacity: 0;
    transform: translateY(28px);
    transition: opacity 0.5s ease, transform 0.5s ease;
}

.news-card--visible {
    opacity: 1;
    transform: translateY(0);
}

.news-card-inner {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    overflow: hidden;
    background: #fff;
    transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.news-card:hover .news-card-inner {
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
    transform: translateY(-3px);
}

.news-card-img-wrap {
    width: 100%;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    background: #000;
    display: flex;
    align-items: center;
    justify-content: center;
}

.news-card-img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    display: block;
    transition: transform 0.3s ease;
}

.news-card:hover .news-card-img {
    transform: scale(1.03);
}

.news-card-img-placeholder {
    width: 100%;
    height: 100%;
    background: #e8e8e8;
}

.news-date {
    font-size: 0.8rem;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.news-title {
    font-size: 1.25rem;
    line-height: 1.2;
    color: var(--site-black);
}

.news-excerpt {
    font-size: 0.92rem;
    color: #444;
    line-height: 1.6;
    margin: 0;
}

.news-author {
    border-top: 1px solid #eee;
}

.news-author-avatar {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
    border: 2px solid #eee;
}

.news-author-avatar-placeholder {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: #ddd;
    flex-shrink: 0;
}

.news-author-label {
    font-size: 0.72rem;
    color: #999;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.news-author-name {
    font-size: 0.95rem;
    color: var(--site-black);
    line-height: 1.2;
}

.paginating {
    opacity: 0.4;
    pointer-events: none;
    transition: opacity 0.15s ease;
}

/* Pagination */
.pagination-btn {
    min-width: 40px;
    height: 40px;
    padding: 0 10px;
    border: 2px solid var(--atlas-blue);
    background: transparent;
    color: var(--atlas-blue);
    font-family: 'Oswald', sans-serif;
    font-size: 0.95rem;
    cursor: pointer;
    transition: background 0.15s, color 0.15s, border-color 0.15s;
    border-radius: 2px;
}

.pagination-btn:hover:not(:disabled):not(.active) {
    background: var(--atlas-blue);
    color: #fff;
}

.pagination-btn.active {
    background: var(--atlas-blue);
    border-color: var(--atlas-blue);
    color: #fff;
}

.pagination-btn:disabled {
    border-color: #ccc;
    color: #ccc;
    cursor: default;
}
</style>
